import { useState, useMemo, useRef, useEffect, memo } from 'react';
import { Link } from 'react-router-dom';
import { ComposableMap, Geographies, Geography, ZoomableGroup } from 'react-simple-maps';
import { ZoomIn, ZoomOut, RotateCcw, X, ArrowRight } from 'lucide-react';
import { COUNTRY_DATA } from '@/components/insights/InteractiveWorldMap';
const GEO_URL = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json';
const STATUS_COLORS = { live: '#16a34a', upcoming: '#f59e0b', monitoring: '#6366f1' };
const STATUS_LABELS = { live: 'Mandate live', upcoming: 'Upcoming', monitoring: 'Monitoring' };
// ISO 2-letter code -> numeric id used by the map shapes.
const ISO_TO_ID = (() => {
  const m = {};
  Object.entries(COUNTRY_DATA).forEach(([id, d]) => { m[d.iso] = id; });
  const extra = {
    AO: '024', PK: '586', BD: '050', IL: '376', QA: '634', BH: '048', KZ: '398', SN: '686', CI: '384',
    ZM: '894', ZW: '716', MU: '480', DO: '214', GT: '320', HN: '340', SV: '222', NI: '558', PY: '600',
    VE: '862', BA: '070', ME: '499', MK: '807', AL: '008', MD: '498', BY: '112', GE: '268', AM: '051',
    AZ: '031', UZ: '860', LK: '144', NP: '524', KH: '116', MM: '104', LA: '418', DZ: '012', TN: '788',
  };
  Object.entries(extra).forEach(([iso, id]) => { if (!m[iso]) m[iso] = id; });
  return m;
})();
const colorOf = (item) => item.status_color || STATUS_COLORS[item.status] || '#6366f1';
const TrackerMap = ({ items, categoryId }) => {
  const [selected, setSelected] = useState(null);
  const [hovered, setHovered] = useState(null);
  const [tipPos, setTipPos] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [center, setCenter] = useState([10, 30]);
  const boxRef = useRef(null);
  const detailRef = useRef(null);
  useEffect(() => { setSelected(null); setHovered(null); }, [categoryId]);
  // When a country is clicked, bring its details into view.
  useEffect(() => {
    if (!selected || !detailRef.current) return;
    const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    detailRef.current.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
  }, [selected]);
  const byId = useMemo(() => {
    const m = {};
    (items || []).forEach((it) => {
      const id = ISO_TO_ID[(it.country_code || '').toUpperCase()];
      if (id) m[id] = it;
    });
    return m;
  }, [items]);
  const legend = useMemo(() => {
    const seen = {};
    (items || []).forEach((it) => {
      if (!seen[it.status]) seen[it.status] = { color: colorOf(it), label: STATUS_LABELS[it.status] || it.status_label || it.status };
    });
    return Object.values(seen);
  }, [items]);
  const iconBtn = 'w-9 h-9 bg-white/90 backdrop-blur-sm border border-slate-200 rounded-lg flex items-center justify-center text-slate-600 hover:bg-white transition-colors';
  return (
    <div ref={boxRef} className="relative" data-testid="tracker-map">
      <div className="absolute top-3 right-3 z-20 flex flex-col gap-1">
        <button type="button" onClick={() => setZoom((z) => Math.min(z * 1.5, 8))} className={iconBtn} aria-label="Zoom in" data-testid="tracker-map-zoom-in"><ZoomIn className="w-4 h-4" /></button>
        <button type="button" onClick={() => setZoom((z) => Math.max(z / 1.5, 1))} className={iconBtn} aria-label="Zoom out" data-testid="tracker-map-zoom-out"><ZoomOut className="w-4 h-4" /></button>
        <button type="button" onClick={() => { setZoom(1); setCenter([10, 30]); }} className={iconBtn} aria-label="Reset map" data-testid="tracker-map-reset"><RotateCcw className="w-4 h-4" /></button>
      </div>
      <div className="absolute bottom-3 left-3 z-20 bg-white/90 backdrop-blur-sm border border-slate-200 rounded-xl px-3 py-2.5">
        <div className="space-y-1.5">
          {legend.map((s) => (
            <div key={s.label} className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-sm shrink-0" style={{ backgroundColor: s.color }} />
              <span className="text-xs text-slate-600">{s.label}</span>
            </div>
          ))}
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-sm shrink-0" style={{ backgroundColor: '#e2e8f0' }} />
            <span className="text-xs text-slate-600">Not tracked</span>
          </div>
        </div>
      </div>
      {hovered && (
        <div className="absolute z-30 pointer-events-none bg-slate-900 text-white text-xs px-3 py-1.5 rounded-lg shadow-lg" style={{ left: tipPos.x + 12, top: tipPos.y - 34 }}>
          {hovered.country_name}
          <span className="ml-1.5 opacity-70">{hovered.status_label}</span>
        </div>
      )}
      <div className="bg-gradient-to-b from-slate-50 to-slate-100 rounded-2xl overflow-hidden border border-slate-200">
        <ComposableMap
          projection="geoMercator"
          projectionConfig={{ scale: 140, center: [10, 30] }}
          style={{ width: '100%', height: 'auto', aspectRatio: '2.2/1' }}
        >
          <ZoomableGroup
            zoom={zoom}
            center={center}
            filterZoomEvent={(e) => e.type !== 'wheel'}
            onMoveEnd={({ coordinates, zoom: z }) => { setCenter(coordinates); setZoom(z); }}
          >
            <Geographies geography={GEO_URL}>
              {({ geographies }) =>
                geographies.map((geo) => {
                  const it = byId[geo.id];
                  const fill = it ? colorOf(it) : '#e2e8f0';
                  return (
                    <Geography
                      key={geo.rsmKey}
                      geography={geo}
                      fill={fill}
                      stroke={selected && it && selected.country_code === it.country_code ? '#0f172a' : '#fff'}
                      strokeWidth={selected && it && selected.country_code === it.country_code ? 1.2 : 0.5}
                      onClick={() => { if (it) setSelected(it); }}
                      onMouseEnter={(e) => {
                        if (!it || !boxRef.current) return;
                        const r = boxRef.current.getBoundingClientRect();
                        setHovered(it);
                        setTipPos({ x: e.clientX - r.left, y: e.clientY - r.top });
                      }}
                      onMouseMove={(e) => {
                        if (!it || !boxRef.current) return;
                        const r = boxRef.current.getBoundingClientRect();
                        setTipPos({ x: e.clientX - r.left, y: e.clientY - r.top });
                      }}
                      onMouseLeave={() => setHovered(null)}
                      style={{
                        default: { cursor: it ? 'pointer' : 'default', outline: 'none', transition: 'fill 0.25s' },
                        hover: { fill, opacity: it ? 0.8 : 1, outline: 'none' },
                        pressed: { fill, opacity: 0.7, outline: 'none' },
                      }}
                      data-testid={it ? `tracker-map-country-${it.country_code.toLowerCase()}` : undefined}
                    />
                  );
                })
              }
            </Geographies>
          </ZoomableGroup>
        </ComposableMap>
      </div>
      {selected ? (
        <div ref={detailRef} className="mt-4 scroll-mt-24 bg-white rounded-2xl border-2 p-5 sm:p-6 relative" style={{ borderColor: colorOf(selected) + '55' }} data-testid="tracker-map-detail">
          <button type="button" onClick={() => setSelected(null)} className="absolute top-3 right-3 p-1.5 text-slate-400 hover:text-slate-600" aria-label="Close" data-testid="tracker-map-detail-close">
            <X className="w-4 h-4" />
          </button>
          <div className="flex items-start gap-4">
            <img
              src={`https://flagcdn.com/48x36/${selected.country_code.toLowerCase()}.png`}
              alt={selected.country_name}
              className="w-12 h-9 rounded-sm object-cover border border-slate-200 shrink-0"
            />
            <div className="flex-1 min-w-0 pr-6">
              <div className="flex flex-wrap items-center gap-2 mb-1">
                <h2 className="font-heading text-xl font-bold text-taxera-charcoal">{selected.country_name}</h2>
                <span className="text-xs px-2 py-0.5 rounded-full font-bold text-white" style={{ backgroundColor: colorOf(selected) }}>{selected.status_label}</span>
                {selected.last_updated && <span className="text-xs text-slate-400">Updated {selected.last_updated}</span>}
              </div>
              {selected.layer1?.mandate_name && <p className="text-sm font-semibold text-slate-700 mb-2">{selected.layer1.mandate_name}</p>}
              {selected.layer1?.summary && <p className="text-sm text-slate-600 mb-4 leading-relaxed">{selected.layer1.summary}</p>}
              {Array.isArray(selected.layer1?.key_dates) && selected.layer1.key_dates.length > 0 && (
                <div className="mb-4" data-testid="tracker-map-detail-dates">
                  <h3 className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-2">Key dates</h3>
                  <ul className="space-y-1.5">
                    {selected.layer1.key_dates.slice(0, 5).map((d, i) => (
                      <li key={`${d.date}-${i}`} className="text-sm text-slate-600">
                        <span className="font-semibold text-slate-800">{d.date}</span>{' '}{d.event}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {selected.layer1?.in_scope && (
                <div className="mb-4" data-testid="tracker-map-detail-scope">
                  <h3 className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-1">Who it applies to</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{selected.layer1.in_scope}</p>
                </div>
              )}
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                <Link
                  to={`/tracker/${categoryId}/${selected.country_code.toLowerCase()}`}
                  className="inline-flex items-center gap-1.5 text-sm font-bold text-taxera-blue hover:gap-2.5 transition-all"
                  data-testid="tracker-map-detail-link"
                >
                  Open the full analysis <ArrowRight className="w-4 h-4" />
                </Link>
                <button
                  type="button"
                  onClick={() => { setSelected(null); if (boxRef.current) boxRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}
                  className="text-sm font-semibold text-slate-500 hover:text-slate-800"
                  data-testid="tracker-map-detail-back"
                >
                  Back to the map
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <p className="mt-3 text-sm text-slate-500" data-testid="tracker-map-hint">Click a country to see what applies there.</p>
      )}
      <p className="mt-3 text-xs text-slate-400 leading-relaxed" data-testid="tracker-map-disclaimer">
        For information only. This is not legal or tax advice. Rules and dates change, so please check with the local tax authority or a qualified adviser.
      </p>
    </div>
  );
};
export default memo(TrackerMap);
